import type { Theme } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import { IconButton, Typography, AnimatePresence, motion, useIsMobile } from '@paintscout/ui'
import React from 'react'

const useStyles = makeStyles<Theme, QuoteSidebarTitleProps>((theme) => ({
  root: {
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 60,
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      paddingBottom: theme.spacing(1),
      paddingTop: `calc(${theme.spacing(1)}px + env(safe-area-inset-top))`
    }
  },
  spacer: {
    width: 40,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      width: ({ pageKey }) => (pageKey === 'index' ? 20 : 40)
    }
  },
  button: {
    padding: theme.spacing(),
    minWidth: 40
  },
  title: {
    position: 'relative',
    width: '100%',
    height: 40,
    fontSize: (props) => (props.shrink ? '1.1rem' : '1.4rem')
  }
}))

export interface QuoteSidebarTitleProps {
  pageKey: string
  title: React.ReactNode
  shrink?: boolean
  onBackClick?: (event: React.MouseEvent) => void
  onCloseClick?: (event: React.MouseEvent) => void
}

function QuoteSidebarTitle(props: QuoteSidebarTitleProps) {
  const { title, pageKey, shrink, onBackClick, onCloseClick } = props
  const classes = useStyles(props)
  const mobile = useIsMobile()
  return (
    <div className={classes.root}>
      {onBackClick ? (
        <IconButton onClick={onBackClick} classes={{ root: classes.button }} color={'default'}>
          <KeyboardArrowLeftIcon />
        </IconButton>
      ) : (
        <div className={classes.spacer} />
      )}

      <Typography variant={'h3'} className={classes.title}>
        {/* animate title change by fade */}
        <AnimatePresence>
          <motion.div
            key={pageKey}
            initial={{
              opacity: 0,
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: mobile && pageKey === 'index' ? 'start' : 'center',
              fontSize: shrink ? '1.1rem' : '1.4rem'
            }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              damping: 300
            }}
          >
            <div>{title}</div>
          </motion.div>
        </AnimatePresence>
      </Typography>

      <IconButton
        onClick={onCloseClick}
        classes={{ root: classes.button }}
        color={'default'}
        data-testid={'sidebar-close-button'}
      >
        {onCloseClick && <CloseIcon />}
      </IconButton>
    </div>
  )
}
export default QuoteSidebarTitle
